import { addDays, format, formatISO } from 'date-fns';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { confirmationSchedule } from '@/domain/confirmationWorklist';
import { eventBus } from '@/events/eventBus';
import { opsItemUrl } from '@/domain/opsThreadLoop';
import { customerName } from '@/utils/format';
import type { Booking } from '@/domain/types';

function nowIso(): string {
  return useSimulationStore.getState().clockIso;
}

/**
 * Deterministic deadline for the artist to get a response before Ops cancels.
 * Prefer the booking's own ops-decision day (vault-06 cadence); if that day has
 * already passed `at` (the common case once the booking has escalated), fall
 * back to a fixed `at + 1 day`. Pure + date-only - no Date.now.
 */
function resolveAskDeadline(booking: Booking, at: string): string {
  const decisionIso = confirmationSchedule(booking).decisionIso;
  if (new Date(decisionIso).getTime() > new Date(at).getTime()) return decisionIso;
  return formatISO(addDays(new Date(at), 1));
}

/**
 * Preset "ask the artist" message body (vault-06 artist-tries path). Names the
 * client, their appointment day, and the response deadline. Australian English,
 * no em dashes.
 */
function buildAskBody(booking: Booking, clientName: string, deadlineIso: string): string {
  const dayLabel = format(new Date(booking.start), 'EEE d MMM');
  const deadlineLabel = format(new Date(deadlineIso), 'EEE d MMM');
  return `${clientName} hasn't confirmed their ${dayLabel} appt. Can you reach out from your IG? Need a response by ${deadlineLabel}.`;
}

/**
 * Ops asks the artist to personally reach out to a client who has not confirmed
 * (tv-var escalation chain, vault System Diagram 06 artist-tries path). Sends a
 * preset, logged, accountable message in the Ops<->artist internal-comms DM with
 * a deadline, and stamps the booking so the modal can show "asked" state. The
 * artist replies in the thread (their real-world IG reach-out) and Ops - never
 * the artist - later marks the booking confirmed.
 *
 * A mutation flow following the src/domain/*Flow.ts convention: read store,
 * mutate, publish. No new activity-feed type is minted - the DM message, the
 * booking stamps, and the event are the record.
 */
export function askArtistToConfirm(
  bookingId: string,
  opts: { byPersonId: string; deadlineIso?: string; at?: string },
): { ok: boolean; threadId?: string } {
  const brain = useBrainStore.getState();
  const booking = brain.bookings[bookingId];
  if (!booking) return { ok: false };
  const artist = brain.artists[booking.artistId];
  if (!artist) return { ok: false };

  const at = opts.at ?? nowIso();
  const deadline = opts.deadlineIso ?? resolveAskDeadline(booking, at);
  const customer = brain.customers[booking.customerId];
  const clientName = customer ? customerName(customer) : 'the client';

  // Open (or reuse) the Ops<->artist DM and send the preset message. The artist
  // person id is the booking's artistId (artists sit in peoplePool by id).
  const threadId = brain.openOrCreateDm([opts.byPersonId, booking.artistId], opts.byPersonId, at);
  brain.sendDmMessage(threadId, buildAskBody(booking, clientName, deadline), {
    authorId: opts.byPersonId,
    timestamp: at,
    mentions: [booking.artistId],
    // W5 mj: tag the ask with the Ops row (the confirmation item id is the
    // booking id) so the row shows awaiting / replied from the thread.
    attachments: [{ kind: 'link', url: opsItemUrl(bookingId), label: 'Ops item' }],
  });

  brain.upsertBooking({ ...booking, artistAskedAt: at, artistAskDeadlineIso: deadline });

  eventBus.publish('confirmation:artist-asked', {
    bookingId,
    artistId: booking.artistId,
    deadline,
    at,
  });

  return { ok: true, threadId };
}
